import React, { useState, useMemo, useEffect } from 'react';
import { TopLandingPagesBar } from '../ChartsSection';
import { fmtNumber, fmtPercent, fmtDuration } from '../shared/format';
import {
  formatLandingPage,
  sortRows, weightedAvg, downloadCsv, downloadPdf, makeExport,
  SortableHeader, PaginationFooter, TableSkeleton, SectionInsightCard,
} from '../shared';
import ThinScroll from '../../shared/ThinScroll';
import { ErrorState, EmptyState } from '../../shared/StateMessage';
import useTablePage from '../../../hooks/useTablePage';
import { expandableRowProps } from '../../shared/expandableRow';
// Page-intent patterns live in an editable data file (spec §13) so the URL→intent
// mapping is maintained in one place, separate from this component's logic. JSON has no
// regex type, so each pattern is stored as { source, flags } and rebuilt into a RegExp
// here once at module load.
import PAGE_INTENT_CONFIG from '../../../config/ga4PageIntentCategories.json';

/* ============================= Page intent classification ============================= */
const PAGE_INTENT_CATEGORIES = PAGE_INTENT_CONFIG.map(({ category, patterns }) => ({
  category,
  patterns: patterns.map(({ source, flags }) => new RegExp(source, flags || '')),
}));

export const resolvePageIntent = (pagePath) => {
  if (!pagePath) return 'Other';
  for (const { category, patterns } of PAGE_INTENT_CATEGORIES) {
    if (patterns.some((pattern) => pattern.test(pagePath))) return category;
  }
  return 'Other';
};

/* ============================= All Pages table ============================= */
const PagesTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'pageviews', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => (row.page_path || '').toLowerCase().includes(query) || (row.page_title || '').toLowerCase().includes(query));
  }, [data, searchQuery]);

  // Must run before the early returns below — hooks can't be called conditionally.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(filtered.length, perPage, [data, searchQuery, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No pages data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);
  const totalItems = sorted.length;
  const paged = sorted.slice(sliceStart, sliceEnd);

  const totals = {
    pageviews: filtered.reduce((sum, row) => sum + (row.pageviews || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    bounce_rate: weightedAvg(filtered, 'bounce_rate', 'pageviews'),
    engagement_rate: weightedAvg(filtered, 'engagement_rate', 'pageviews'),
    avg_session_duration: weightedAvg(filtered, 'avg_session_duration', 'pageviews'),
    conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
  };

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} page{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm table-fixed min-w-[800px]">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Page" sortKey="page_path" currentSort={sort} onSort={handleSort} className="w-[280px]" />
              <SortableHeader label="Pageviews" sortKey="pageviews" currentSort={sort} onSort={handleSort} className="w-[90px]" />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} className="w-[80px]" />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} className="w-[95px]" />
              <SortableHeader label="Engagement" sortKey="engagement_rate" currentSort={sort} onSort={handleSort} className="w-[100px]" />
              <SortableHeader label="Avg Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} className="w-[100px]" />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} className="w-[95px]" />
            </tr>
          </thead>
          <tbody>
            {paged.map((row, index) => (
              <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Page" className="px-3 py-2.5">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={row.page_title || row.page_path}>
                    {formatLandingPage(row.page_path)}
                  </div>
                  {row.page_title && (
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 truncate" title={row.page_title}>
                      {row.page_title}
                    </div>
                  )}
                </td>
                <td data-label="Pageviews" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.pageviews)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Engagement" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtPercent(row.engagement_rate)}</td>
                <td data-label="Avg Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtDuration(row.avg_session_duration)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300 whitespace-nowrap">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({totalItems} pages)</td>
              <td data-label="Pageviews" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtNumber(totals.pageviews)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtNumber(totals.users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtPercent(totals.bounce_rate)}</td>
              <td data-label="Engagement" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtPercent(totals.engagement_rate)}</td>
              <td data-label="Avg Duration" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtDuration(totals.avg_session_duration)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100 whitespace-nowrap">{fmtNumber(totals.conversions)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="pages" />
    </div>
  );
};

/* ============================= Landing Pages table ============================= */
const LandingPagesTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'sessions', dir: 'desc' });
  const perPage = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => formatLandingPage(row.landing_page).toLowerCase().includes(query));
  }, [data, searchQuery]);

  // Must run before the early returns below — hooks can't be called conditionally.
  const { page, setPage, totalPages, sliceStart, sliceEnd } = useTablePage(filtered.length, perPage, [data, searchQuery, sort]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No landing pages data for this period." padding="py-12" />;
  }

  const sorted = sortRows(filtered, sort.key, sort.dir);
  const totalItems = sorted.length;
  const paged = sorted.slice(sliceStart, sliceEnd);

  const totals = {
    sessions: filtered.reduce((sum, row) => sum + (row.sessions || 0), 0),
    users: filtered.reduce((sum, row) => sum + (row.users || 0), 0),
    new_users: filtered.reduce((sum, row) => sum + (row.new_users || 0), 0),
    bounce_rate: weightedAvg(filtered, 'bounce_rate', 'sessions'),
    avg_session_duration: weightedAvg(filtered, 'avg_session_duration', 'sessions'),
    conversions: filtered.reduce((sum, row) => sum + (row.conversions || 0), 0),
  };

  return (
    <div>
      <div className="text-xs text-gray-400 dark:text-gray-500 mb-2">{totalItems} landing page{totalItems !== 1 ? 's' : ''}</div>
      <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
        <table className="responsive-card-table w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
              <SortableHeader label="Landing Page" sortKey="landing_page" currentSort={sort} onSort={handleSort} className="min-w-[220px]" />
              <SortableHeader label="Sessions" sortKey="sessions" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="New Users" sortKey="new_users" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Avg. Duration" sortKey="avg_session_duration" currentSort={sort} onSort={handleSort} />
              <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
            </tr>
          </thead>
          <tbody>
            {paged.map((row, index) => (
              <tr key={index} className="border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition">
                <td data-label="Landing Page" className="px-3 py-2.5 max-w-[320px]">
                  <div className="font-medium text-gray-900 dark:text-gray-100 truncate" title={formatLandingPage(row.landing_page)}>
                    {formatLandingPage(row.landing_page)}
                  </div>
                </td>
                <td data-label="Sessions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.sessions)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.users)}</td>
                <td data-label="New Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.new_users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(row.bounce_rate)}</td>
                <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtDuration(row.avg_session_duration)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(row.conversions)}</td>
              </tr>
            ))}
            <tr className="bg-gray-50 dark:bg-gray-800/70 font-semibold">
              <td data-label="Total" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">Total ({totalItems})</td>
              <td data-label="Sessions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.sessions)}</td>
              <td data-label="Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.users)}</td>
              <td data-label="New Users" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.new_users)}</td>
              <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtPercent(totals.bounce_rate)}</td>
              <td data-label="Avg. Duration" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtDuration(totals.avg_session_duration)}</td>
              <td data-label="Conversions" className="px-3 py-2.5 text-gray-900 dark:text-gray-100">{fmtNumber(totals.conversions)}</td>
            </tr>
          </tbody>
        </table>
      </ThinScroll>
      <PaginationFooter pagination={{ total_items: totalItems, total_pages: totalPages }} page={page} perPage={perPage} onPageChange={setPage} label="landing pages" />
    </div>
  );
};

/* ============================= Pages by Intent ============================= */
const PagesByIntentTable = ({ data, loading, error, searchQuery = '' }) => {
  const [sort, setSort] = useState({ key: 'pageviews', dir: 'desc' });
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [drillPage, setDrillPage] = useState({});
  const DRILL_PAGE_SIZE = 10;

  const handleSort = (key) => {
    setSort((prev) => prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (!searchQuery) return data;
    const query = searchQuery.toLowerCase();
    return data.filter(row => (row.page_path || '').toLowerCase().includes(query) || (row.page_title || '').toLowerCase().includes(query) || resolvePageIntent(row.page_path).toLowerCase().includes(query));
  }, [data, searchQuery]);

  const { categories, categoryDetails } = useMemo(() => {
    if (!filtered || filtered.length === 0) return { categories: [], categoryDetails: {} };
    const grouped = {};
    for (const row of filtered) {
      const category = resolvePageIntent(row.page_path);
      if (!grouped[category]) {
        grouped[category] = {
          category, pages: 0, pageviews: 0, users: 0,
          bounceNumerator: 0, sessionDenominator: 0, conversions: 0, rows: []
        };
      }
      grouped[category].pages += 1;
      grouped[category].pageviews += row.pageviews || 0;
      grouped[category].users += row.users || 0;
      grouped[category].conversions += row.conversions || 0;
      if (row.bounce_rate != null && row.pageviews != null) {
        grouped[category].bounceNumerator += row.bounce_rate * row.pageviews;
        grouped[category].sessionDenominator += row.pageviews;
      }
      grouped[category].rows.push(row);
    }
    for (const cat of Object.values(grouped)) {
      cat.bounce_rate = cat.sessionDenominator > 0 ? cat.bounceNumerator / cat.sessionDenominator : null;
    }
    const catList = Object.values(grouped);
    const details = {};
    for (const cat of catList) { details[cat.category] = cat.rows; }
    return { categories: catList, categoryDetails: details };
  }, [filtered]);

  if (error) return <ErrorState message={error} />;
  if (loading) return <TableSkeleton />;
  if (!data || data.length === 0) {
    return <EmptyState message="No pages data for this period." padding="py-12" />;
  }

  const sorted = sortRows(categories, sort.key, sort.dir);

  return (
    <ThinScroll className="responsive-card-table-wrapper bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-x-auto">
      <table className="responsive-card-table w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
            <SortableHeader label="Intent Category" sortKey="category" currentSort={sort} onSort={handleSort} className="min-w-[180px]" />
            <SortableHeader label="Pages" sortKey="pages" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Pageviews" sortKey="pageviews" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Users" sortKey="users" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Bounce Rate" sortKey="bounce_rate" currentSort={sort} onSort={handleSort} />
            <SortableHeader label="Conversions" sortKey="conversions" currentSort={sort} onSort={handleSort} />
          </tr>
        </thead>
        <tbody>
          {sorted.map((cat) => (
            <React.Fragment key={cat.category}>
              <tr
                {...expandableRowProps({
                  expanded: expandedCategory === cat.category,
                  onToggle: () => {
                    const isCollapsing = expandedCategory === cat.category;
                    setExpandedCategory(isCollapsing ? null : cat.category);
                    if (!isCollapsing) setDrillPage(prev => ({ ...prev, [cat.category]: 0 }));
                  },
                  label: `pages in ${cat.category}`,
                  className: `border-b dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition cursor-pointer ${expandedCategory === cat.category ? '' : 'rc-collapsed'}`,
                })}
              >
                <td data-label="Intent Category" className={`rc-title-cell px-3 py-2.5 ${expandedCategory === cat.category ? 'rc-title-open' : ''}`}>
                  <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 dark:text-gray-100">
                    <svg className={`w-3.5 h-3.5 transition-transform ${expandedCategory === cat.category ? 'rotate-90' : ''}`} viewBox="0 0 12 12">
                      <path d="M4 2l4 4-4 4" stroke="currentColor" strokeWidth="1.5" fill="none" />
                    </svg>
                    {cat.category}
                  </span>
                </td>
                <td data-label="Pages" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(cat.pages)}</td>
                <td data-label="Pageviews" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(cat.pageviews)}</td>
                <td data-label="Users" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(cat.users)}</td>
                <td data-label="Bounce Rate" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtPercent(cat.bounce_rate)}</td>
                <td data-label="Conversions" className="px-3 py-2.5 text-gray-700 dark:text-gray-300">{fmtNumber(cat.conversions)}</td>
              </tr>
              {expandedCategory === cat.category && (() => {
                const allRows = categoryDetails[cat.category] || [];
                const currentPage = drillPage[cat.category] || 1;
                const totalPages = Math.ceil(allRows.length / DRILL_PAGE_SIZE);
                const pageRows = allRows.slice((currentPage - 1) * DRILL_PAGE_SIZE, currentPage * DRILL_PAGE_SIZE);
                return (
                  <>
                    {pageRows.map((row, index) => (
                      <tr key={index} className="border-b dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30">
                        <td data-label="Page" className="px-3 py-2 pl-9 max-w-[300px] max-[499px]:!pl-3">
                          <div className="text-gray-700 dark:text-gray-300 truncate text-xs" title={row.page_title || row.page_path}>
                            {formatLandingPage(row.page_path)}
                          </div>
                          {row.page_title && (
                            <div className="text-[10px] text-gray-400 dark:text-gray-500 truncate" title={row.page_title}>
                              {row.page_title}
                            </div>
                          )}
                        </td>
                        <td data-label="Pages" className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">—</td>
                        <td data-label="Pageviews" className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{fmtNumber(row.pageviews)}</td>
                        <td data-label="Users" className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{fmtNumber(row.users)}</td>
                        <td data-label="Bounce Rate" className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{fmtPercent(row.bounce_rate)}</td>
                        <td data-label="Conversions" className="px-3 py-2 text-gray-500 dark:text-gray-400 text-xs">{fmtNumber(row.conversions)}</td>
                      </tr>
                    ))}
                    {totalPages > 1 && (
                      <tr className="bg-gray-50/50 dark:bg-gray-800/30">
                        <td colSpan={6} className="px-3 py-0" onClick={(event) => event.stopPropagation()}>
                          <PaginationFooter
                            pagination={{ total_items: allRows.length, total_pages: totalPages }}
                            page={currentPage}
                            perPage={DRILL_PAGE_SIZE}
                            onPageChange={(nextPage) => setDrillPage((prev) => ({ ...prev, [cat.category]: nextPage }))}
                            label="pages"
                          />
                        </td>
                      </tr>
                    )}
                  </>
                );
              })()}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </ThinScroll>
  );
};

/* ============================= Insight summary ============================= */
export const getPagesSummary = (landingData) => {
  if (!landingData || landingData.length === 0) return { summary: [], warnings: [] };
  const summary = [];
  const warnings = [];

  // Drop unattributed rows from the insight (still rendered in the table).
  const isUnattributed = (raw) => {
    if (raw == null) return true;
    const normalized = String(raw).trim().toLowerCase();
    return normalized === '' || normalized === '(not set)' || normalized === '(unknown)' || normalized === '(other)';
  };
  // Threshold below which a bounce rate isn't statistically meaningful.
  const MIN_SESSIONS = 100;

  // Dedup by formatted label so we don't surface the same page twice.
  const dedup = new Map();
  for (const row of landingData) {
    const raw = row.landing_page ?? row.page_path ?? null;
    if (isUnattributed(raw)) continue;
    const key = formatLandingPage(raw);
    const existing = dedup.get(key);
    if (!existing) {
      dedup.set(key, { ...row, _label: key, sessions: row.sessions || 0 });
    } else {
      // Sessions-weighted bounce when the same label appears more than once.
      const totSessions = existing.sessions + (row.sessions || 0);
      const weightedBounce = totSessions > 0
        ? ((existing.bounce_rate || 0) * existing.sessions + (row.bounce_rate || 0) * (row.sessions || 0)) / totSessions
        : existing.bounce_rate || 0;
      dedup.set(key, { ...existing, sessions: totSessions, bounce_rate: weightedBounce });
    }
  }
  const rows = Array.from(dedup.values());
  if (rows.length === 0) return { summary, warnings };

  const total = rows.reduce((sum, row) => sum + (row.sessions || 0), 0);
  const sorted = [...rows].sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0));
  const top = sorted[0];

  // Sentence 1 — top page (always show).
  if (top && total > 0) {
    const pct = ((top.sessions / total) * 100).toFixed(1);
    let text = `${top._label} dominates traffic (${pct}% of sessions)`;
    if ((top.bounce_rate || 0) > 0.65) text += ` but has a ${(top.bounce_rate * 100).toFixed(0)}% bounce rate`;
    text += '.';
    summary.push(text);
  }

  // Sentence 2 — at most one other high-bounce page with meaningful volume.
  const HIGH_BOUNCE = 0.6;
  const highBounce = rows
    .filter(row => row._label !== top?._label && (row.bounce_rate || 0) > HIGH_BOUNCE && (row.sessions || 0) >= MIN_SESSIONS)
    .sort((rowA, rowB) => (rowB.sessions || 0) - (rowA.sessions || 0));
  if (highBounce.length > 0) {
    const page = highBounce[0];
    const label = page._label.length > 40 ? page._label.slice(0, 37) + '…' : page._label;
    const more = highBounce.length > 1 ? ` (${highBounce.length - 1} other page${highBounce.length - 1 > 1 ? 's' : ''} also above ${Math.round(HIGH_BOUNCE * 100)}%)` : '';
    summary.push(`${label} also bounces high at ${(page.bounce_rate * 100).toFixed(0)}% on ${page.sessions.toLocaleString()} sessions${more}.`);
  }

  if (highBounce.length > 0) {
    const count = highBounce.length;
    warnings.push({ color: 'amber', text: `${count} page${count > 1 ? 's' : ''} with >${Math.round(HIGH_BOUNCE * 100)}% bounce rate (≥${MIN_SESSIONS} sessions) — review content relevance and load times` });
  }
  return { summary, warnings };
};

/* ============================= Export helpers ============================= */
// Two column specs — the export switches shape based on which dataset is present.
// Landing pages (sessions-based) vs page paths (pageviews + engagement). Each spec
// drives both CSV (raw + full headers) and PDF (formatted + abbreviated headers).
const LANDING_PAGES_EXPORT = makeExport([
  { header: 'Landing Page', get: (row) => formatLandingPage(row.landing_page) },
  { header: 'Sessions', get: (row) => row.sessions, pdf: (row) => fmtNumber(row.sessions) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'New Users', get: (row) => row.new_users, pdf: (row) => fmtNumber(row.new_users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Avg Duration', pdfHeader: 'Dur.', get: (row) => row.avg_session_duration, pdf: (row) => fmtDuration(row.avg_session_duration) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

const PAGE_PATHS_EXPORT = makeExport([
  { header: 'Page Path', get: (row) => row.page_path },
  { header: 'Pageviews', pdfHeader: 'PVs', get: (row) => row.pageviews, pdf: (row) => fmtNumber(row.pageviews) },
  { header: 'Users', get: (row) => row.users, pdf: (row) => fmtNumber(row.users) },
  { header: 'Bounce Rate', pdfHeader: 'Bounce', get: (row) => (row.bounce_rate != null ? (row.bounce_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.bounce_rate) },
  { header: 'Engagement Rate', pdfHeader: 'Engage', get: (row) => (row.engagement_rate != null ? (row.engagement_rate * 100).toFixed(1) + '%' : ''), pdf: (row) => fmtPercent(row.engagement_rate) },
  { header: 'Avg Duration', pdfHeader: 'Dur.', get: (row) => row.avg_session_duration, pdf: (row) => fmtDuration(row.avg_session_duration) },
  { header: 'Conversions', pdfHeader: 'Conv.', get: (row) => row.conversions, pdf: (row) => fmtNumber(row.conversions) },
]);

export const exportPagesCsv = (pagesData, landingPagesData, rangeStart, rangeEnd) => {
  if (!pagesData?.length && !landingPagesData?.length) return;
  const useLanding = !!landingPagesData?.length;
  const spec = useLanding ? LANDING_PAGES_EXPORT : PAGE_PATHS_EXPORT;
  const exportData = useLanding ? landingPagesData : pagesData;
  downloadCsv(spec.headers, exportData.map(spec.toCsvRow), `ga4-pages-${rangeStart}-to-${rangeEnd}.csv`);
};

export const exportPagesPdf = (pagesData, landingPagesData, rangeStart, rangeEnd) => {
  if (!pagesData?.length && !landingPagesData?.length) return;
  const dateRange = rangeStart && rangeEnd ? `${rangeStart} to ${rangeEnd}` : '';
  const useLanding = !!landingPagesData?.length;
  const spec = useLanding ? LANDING_PAGES_EXPORT : PAGE_PATHS_EXPORT;
  const exportData = useLanding ? landingPagesData : pagesData;
  downloadPdf('Pages', spec.pdfHeaders, exportData.map(spec.toPdfRow), `ga4-pages-${rangeStart}-to-${rangeEnd}.pdf`, dateRange);
};

/* ============================= Tab component ============================= */
const PagesTab = ({ pagesData, pagesLoading, pagesError, landingPagesData, landingPagesLoading, landingPagesError, searchQuery }) => {
  const [subTab, setSubTab] = useState('all_pages');

  const subTabs = [
    { id: 'all_pages', label: 'All Pages' },
    { id: 'landing_pages', label: 'Landing Pages' },
    { id: 'pages_by_intent', label: 'Pages by Intent' },
  ];

  return (
    <div>
      <SectionInsightCard title="Pages Insight" {...getPagesSummary(landingPagesData)} />
      <div className="mb-6">
        <TopLandingPagesBar data={landingPagesData} loading={landingPagesLoading} />
      </div>

      <div className="flex items-center gap-1.5 mb-4">
        {subTabs.map((subTabItem) => (
          <button
            key={subTabItem.id}
            type="button"
            onClick={() => setSubTab(subTabItem.id)}
            className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${
              subTab === subTabItem.id
                ? 'bg-[#32B1FF]/10 text-[#32B1FF] border border-[#32B1FF]/30'
                : 'bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent'
            }`}
          >
            {subTabItem.label}
          </button>
        ))}
      </div>

      {subTab === 'all_pages' && (
        <PagesTable data={pagesData} loading={pagesLoading} error={pagesError} searchQuery={searchQuery} />
      )}
      {subTab === 'landing_pages' && (
        <LandingPagesTable data={landingPagesData} loading={landingPagesLoading} error={landingPagesError} searchQuery={searchQuery} />
      )}
      {subTab === 'pages_by_intent' && (
        <PagesByIntentTable data={pagesData} loading={pagesLoading} error={pagesError} searchQuery={searchQuery} />
      )}
    </div>
  );
};

export default PagesTab;
